import React, { useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Check, PriorityHigh } from '@material-ui/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareSquare } from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';
import { ProjectArchiveStatus } from '../../api-client/autogenerated';
import { openFileInNewTab, parseDate } from '../../scripts/utils';
import DownloadIcon from '../icons/Download-icon';

export const EXPORTS_PER_PAGE = 5;

/** One row of an export log, whatever was exported. */
export type ExportLogEntry = {
  id: string;
  generatedOn?: string | null;
  generatedByUserName?: string;
  status: ProjectArchiveStatus;
  percentComplete?: number;
  fileId?: string;
  error?: string;
  isCurrent: boolean;
};

type ExportLogTableProps<T extends ExportLogEntry> = {
  entries: T[];
  isLoading: boolean;
  emptyMessage: string;
  /** Why a stale export is stale, in terms of what was exported. */
  staleTooltip: string;
  describeContents: (entry: T) => string;
  onShowContents: (entry: T) => void;
  /** Offered only where an export can be shared. */
  onShare?: (entry: T) => void;
};

const useStyles = makeStyles((theme) =>
  createStyles({
    tableContainer: {
      // The status and the actions must never be the ones that get clipped.
      overflowX: 'auto',
      border: '1px solid #949494',
      borderRadius: 4,
    },
    cell: {
      whiteSpace: 'nowrap',
      paddingTop: 4,
      paddingBottom: 4,
    },
    selectionCell: {
      maxWidth: 190,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      paddingTop: 4,
      paddingBottom: 4,
    },
    selectionButton: {
      background: 'none',
      border: 'none',
      padding: 0,
      font: 'inherit',
      cursor: 'pointer',
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
    iconButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 0,
      display: 'inline-flex',
      alignItems: 'center',
      marginLeft: 12,
    },
    ready: {
      color: theme.palette.primary.main,
      fontWeight: 500,
    },
    unavailable: {
      color: '#616061',
    },
    failed: {
      color: '#ED3F26',
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      color: '#2C69D6',
      marginTop: 8,
    },
    currencyCell: {
      width: 42,
      paddingTop: 4,
      paddingBottom: 4,
      paddingRight: 0,
    },
    currencyCheckbox: {
      padding: 0,
      // The box carries its own colour, so the disabled grey must not wash it out.
      '&.Mui-disabled': {
        color: 'inherit',
      },
    },
    currencyBox: {
      width: 18,
      height: 18,
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#FFFFFF',
    },
    currentBox: {
      backgroundColor: '#2BB073',
    },
    supersededBox: {
      backgroundColor: '#ED3F26',
    },
    currencyGlyph: {
      fontSize: 16,
    },
  }),
);

/** Only a finished export with a file behind it can be downloaded or shared. */
const isReady = (entry: ExportLogEntry) =>
  entry.status === ProjectArchiveStatus.Complete && !!entry.fileId;

const getStatusLabel = (entry: ExportLogEntry) => {
  switch (entry.status) {
    case ProjectArchiveStatus.Complete:
      return entry.fileId ? 'Ready' : 'File unavailable';
    case ProjectArchiveStatus.InProgress:
      return entry.percentComplete !== undefined && entry.percentComplete !== null
        ? `In progress — ${entry.percentComplete}%`
        : 'In progress';
    case ProjectArchiveStatus.Stalled:
      return 'Did not finish';
    case ProjectArchiveStatus.Failed:
    default:
      return 'Failed';
  }
};

/**
 * An export log: past exports with whether each still reflects what was exported, how far along it
 * is, and a download once it is ready. Shared by the project and punch list export dialogs so the
 * two read the same way.
 */
export default function ExportLogTable<T extends ExportLogEntry>(props: ExportLogTableProps<T>) {
  const {
    entries,
    isLoading,
    emptyMessage,
    staleTooltip,
    describeContents,
    onShowContents,
    onShare,
  } = props;
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const pageCount = Math.ceil(entries.length / EXPORTS_PER_PAGE);

  // Exports finishing while the dialog is open can shrink the list out from under the page.
  useEffect(() => {
    if (page > 0 && page >= pageCount) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);

  const visibleEntries = useMemo(
    () => entries.slice(page * EXPORTS_PER_PAGE, page * EXPORTS_PER_PAGE + EXPORTS_PER_PAGE),
    [entries, page],
  );

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <Typography className={classes.unavailable} style={{ paddingBottom: 8 }}>
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <>
      <div className={classes.tableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.currencyCell} />
              <TableCell className={classes.cell}>Generated</TableCell>
              <TableCell className={classes.cell}>By</TableCell>
              <TableCell className={classes.cell}>Includes</TableCell>
              <TableCell className={classes.cell}>Status</TableCell>
              <TableCell className={classes.cell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleEntries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell className={classes.currencyCell}>
                  <Tooltip
                    arrow
                    placement="top"
                    title={entry.isCurrent ? 'Current export' : staleTooltip}
                  >
                    {/* Tooltips need an enabled element to hang off, so the box wraps it. */}
                    <span>
                      <Checkbox
                        disabled
                        checked
                        disableRipple
                        className={classes.currencyCheckbox}
                        aria-label={entry.isCurrent ? 'Current export' : 'Not current'}
                        checkedIcon={
                          <span
                            className={clsx(
                              classes.currencyBox,
                              entry.isCurrent ? classes.currentBox : classes.supersededBox,
                            )}
                          >
                            {entry.isCurrent ? (
                              <Check className={classes.currencyGlyph} />
                            ) : (
                              <PriorityHigh className={classes.currencyGlyph} />
                            )}
                          </span>
                        }
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell className={classes.cell}>
                  {entry.generatedOn
                    ? parseDate(entry.generatedOn).format('MM/DD/YYYY h:mm A')
                    : '—'}
                </TableCell>
                <TableCell className={classes.cell}>{entry.generatedByUserName ?? '—'}</TableCell>
                <TableCell className={classes.selectionCell}>
                  <Tooltip arrow title="See everything this export contains" placement="top">
                    <button
                      type="button"
                      className={classes.selectionButton}
                      onClick={() => onShowContents(entry)}
                    >
                      {describeContents(entry)}
                    </button>
                  </Tooltip>
                </TableCell>
                <TableCell className={classes.cell}>
                  <Tooltip arrow title={entry.error ?? ''} placement="top">
                    <span
                      className={
                        isReady(entry)
                          ? classes.ready
                          : entry.status === ProjectArchiveStatus.Failed
                          ? classes.failed
                          : classes.unavailable
                      }
                    >
                      {getStatusLabel(entry)}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell className={classes.cell} align="right">
                  {/* Nothing to download or share until the export is actually ready. */}
                  {isReady(entry) && (
                    <>
                      <Tooltip arrow title="Download this export" placement="top">
                        <button
                          type="button"
                          aria-label="Download this export"
                          className={classes.iconButton}
                          onClick={() => openFileInNewTab(entry.fileId!)}
                        >
                          <DownloadIcon />
                        </button>
                      </Tooltip>
                      {onShare && (
                        <Tooltip arrow title="Get a link to this export" placement="top">
                          <button
                            type="button"
                            aria-label="Get a link to this export"
                            className={classes.iconButton}
                            onClick={() => onShare(entry)}
                          >
                            <FontAwesomeIcon
                              icon={faShareSquare}
                              style={{ width: 18, height: 18, color: '#7A797A' }}
                            />
                          </button>
                        </Tooltip>
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {pageCount > 1 && (
        <Pagination
          count={pageCount}
          shape="rounded"
          color="primary"
          size="small"
          page={page + 1}
          onChange={(_event, newPage) => setPage(newPage - 1)}
          className={classes.pagination}
        />
      )}
    </>
  );
}
